'use client'

import { useEffect, useRef, useState } from 'react'

import { useQueryClient } from '@tanstack/react-query'

import { SimSelect } from '~/app/settings/team/banks/BanksSimulator/components/SimSelect'
import {
  deriveCurrency,
  deriveKycType,
  deriveStageLabel,
  deriveStateId,
  isSimulating,
  setSimulating,
} from '~/app/settings/team/banks/BanksSimulator/helpers'
import {
  CURRENCIES,
  KYC_TYPES,
  STATES,
} from '~/app/settings/team/banks/BanksSimulator/mock-data'
import {
  BridgeData,
  CurrencyPreset,
  KycTypePreset,
} from '~/app/settings/team/banks/BanksSimulator/types'
import { Pill } from '~/components/component-library'
import { Button } from '~/components/component-library'
import { QUERY_KEYS } from '~/constants/react-query'
import { IS_PRODUCTION } from '~/constants/system'
import { useActiveOrg, useActiveOrgUpdates } from '~/hooks/org'
import { useAuthUser } from '~/hooks/useAuthUser'
import { useDevtoolsVisible } from '~/hooks/useDevtoolsVisible'
import { useSmartAccounts } from '~/hooks/useSmartAccounts'

export { isSimulating }

export const BanksSimulator = () => {
  const { user } = useAuthUser()
  const { org } = useActiveOrg()
  const { updateBridgeData } = useActiveOrgUpdates()
  const queryClient = useQueryClient()
  const { smartAccounts } = useSmartAccounts({ orderedByValue: true })
  const [activeState, setActiveState] = useState<string | null>(null)
  const devtoolsVisible = useDevtoolsVisible()

  const defaultAddress = smartAccounts[0]?.address ?? ''
  const bridge = org?.bridge

  // Snapshot original bridge data once (null = not yet captured)
  const originalBridgeRef = useRef<BridgeData | null>(null)
  const hasCapturedRef = useRef(false)
  if (!hasCapturedRef.current) {
    originalBridgeRef.current = bridge ? structuredClone(bridge) : undefined
    hasCapturedRef.current = true
  }

  // Keep derived values in a ref so the cache subscription can read them
  const derivedValuesRef = useRef({
    activeState,
    currency: deriveCurrency(bridge),
    kycType: deriveKycType(bridge),
    address: defaultAddress,
  })
  derivedValuesRef.current = {
    activeState,
    currency: deriveCurrency(bridge),
    kycType: deriveKycType(bridge),
    address: defaultAddress,
  }

  // Re-entrancy guard for cache subscription
  const isApplyingRef = useRef(false)

  // Reset simulation state on unmount to prevent stale isSimulating() flag
  useEffect(() => {
    return () => {
      setSimulating(false)
    }
  }, [])

  // Intercept server refetches to re-apply mock data before React re-renders
  useEffect(() => {
    const unsubscribe = queryClient.getQueryCache().subscribe((event) => {
      if (isApplyingRef.current) return
      if (
        event.type === 'updated' &&
        Array.isArray(event.query.queryKey) &&
        event.query.queryKey[0] === QUERY_KEYS.ACTIVE_ORG &&
        derivedValuesRef.current.activeState
      ) {
        const state = STATES.find(
          (s) => s.id === derivedValuesRef.current.activeState,
        )
        if (state) {
          isApplyingRef.current = true
          updateBridgeData(
            state.build(
              derivedValuesRef.current.currency,
              derivedValuesRef.current.kycType,
              derivedValuesRef.current.address,
            ),
          )
          isApplyingRef.current = false
        }
      }
    })
    return () => unsubscribe()
  }, [queryClient, updateBridgeData])

  if (IS_PRODUCTION || !user?.isSplitsAdmin || !devtoolsVisible) return null

  const apply = (
    stateId: string,
    currency: CurrencyPreset,
    kycType: KycTypePreset = deriveKycType(bridge),
  ) => {
    const state = STATES.find((s) => s.id === stateId)
    if (!state) return
    isApplyingRef.current = true
    updateBridgeData(state.build(currency, kycType, defaultAddress))
    isApplyingRef.current = false
    setSimulating(true)
    setActiveState(stateId)
  }

  const reset = () => {
    setSimulating(false)
    isApplyingRef.current = true
    updateBridgeData(originalBridgeRef.current ?? undefined)
    isApplyingRef.current = false
    setActiveState(null)
  }

  const realStageLabel = deriveStageLabel(originalBridgeRef.current ?? bridge)
  const stageLabel = activeState
    ? (STATES.find((s) => s.id === activeState)?.label ?? 'Stage')
    : realStageLabel
  const isViewingDifferentStage =
    activeState !== null && stageLabel !== realStageLabel
  const isNotStarted = activeState === 'not-started'
  const activeCurrency = deriveCurrency(bridge)
  const activeKycType = deriveKycType(bridge)
  const effectiveStateId = activeState ?? deriveStateId(bridge)

  return (
    <div className="fixed bottom-4 right-20 z-50 flex items-center gap-2">
      {isViewingDifferentStage && (
        <Pill
          variant="warning"
          size="small"
          label="Viewing in a read-only state"
        />
      )}

      {activeState !== null && (
        <Button variant="secondary" size="small" onClick={reset}>
          Reset
        </Button>
      )}

      {!isNotStarted && (
        <SimSelect
          label={KYC_TYPES.find((t) => t.id === activeKycType)?.label ?? 'KYC'}
          items={KYC_TYPES}
          activeId={activeKycType}
          onSelect={(id) =>
            apply(effectiveStateId, activeCurrency, id as KycTypePreset)
          }
        />
      )}

      {!isNotStarted && (
        <SimSelect
          label={CURRENCIES.find((c) => c.id === activeCurrency)?.label ?? 'US'}
          items={CURRENCIES}
          activeId={activeCurrency}
          onSelect={(id) => apply(effectiveStateId, id as CurrencyPreset)}
        />
      )}

      <SimSelect
        label={stageLabel}
        items={STATES}
        activeId={activeState}
        onSelect={(id) => apply(id, activeCurrency)}
      />
    </div>
  )
}
